import { Environment, RecordSource, Store } from "relay-runtime";
import {
  authMiddleware,
  cacheMiddleware,
  RelayNetworkLayer,
  urlMiddleware,
} from "react-relay-network-modern";
import { jwtDecode } from "jwt-decode";
import { api } from "./utils/constants";
import { store as reduxStore } from "./index";
import { setTokenData } from "./store/Actions/AuthActions";
import { clearUserData } from "./store/Actions/CommonAction";
import { clearBookingDataOnLogoutAction } from "./store/Actions/BookingAction";
import { postBookingDataAction } from "./store/Actions/CommonAction";
import LogoutUserMutation from "./services/mutation/LogoutUserMutation";

const oneMinute = 60 * 1000 * 10;
const REFRESH_THRESHOLD = 2 * 60 * 1000; // 2 minutes

/* -------------------------------------------------------------------------- */
/* 🔁 REFRESH TOKEN FLOW                                                      */
/* -------------------------------------------------------------------------- */
const refreshTokenFlow = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const response = await fetch(api, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: `
          mutation RefreshTokenMutation($refreshToken:String!) {
            refreshToken(refreshToken:$refreshToken) {
              token
              refreshToken
              message
            }
          }`,
        variables: { refreshToken },
      }),
    });

    const json = await response.json();
    const refreshed = json?.data?.refreshToken;

    if (refreshed?.token) {
      localStorage.setItem("userToken", refreshed.token);
      localStorage.setItem("refreshToken", refreshed.refreshToken);

      reduxStore.dispatch(
        setTokenData({
          token: refreshed.token,
          refreshToken: refreshed.refreshToken,
        })
      );

      console.log("✅ Token refreshed successfully");
      return refreshed.token;
    } else {
      console.warn("⚠️ Token refresh failed");
      await cleanupUserData();
      return null;
    }
  } catch (error) {
    console.error("⚠️ Token refresh error — logging out", error);
    await cleanupUserData();
    return null;
  }
};

/* -------------------------------------------------------------------------- */
/* 🧠 CREATE RELAY ENVIRONMENT                                                */
/* -------------------------------------------------------------------------- */
export const createRelayEnvironment = () => {
  const network = new RelayNetworkLayer(
    [
      cacheMiddleware({
        size: 1024,
        ttl: oneMinute,
      }),

      urlMiddleware({
        url: () => Promise.resolve(api),
        headers: {
          Accept: "application/json",
        },
      }),

      authMiddleware({
        token: async () => {
          const token = localStorage.getItem("userToken");
          if (!token || token === "null" || token === "undefined") return null;

          try {
            const decoded = jwtDecode(token);
            const now = Date.now();
            const exp = decoded.exp * 1000;

            // 🔁 Refresh token 2 minutes before expiry
            if (exp - now < REFRESH_THRESHOLD && exp - now > 0) {
              return await refreshTokenFlow();
            }

            // 🚪 If token expired, log out user
            if (now >= exp) {
              alert("Session Expired");
              await cleanupUserData();
              return null;
            }

            return token;
          } catch {
            await cleanupUserData();
            return null;
          }
        },

        tokenRefreshPromise: async () => {
          // Called automatically by relay if 401 happens mid-request
          return await refreshTokenFlow();
        },

        prefix: "JWT ",
        allowEmptyToken: true,
        header: "Authorization",
        getAuthorizationForToken: (token) => (token ? `JWT ${token}` : null),
      }),

      // ✅ Drop invalid or missing auth headers
      (next) => async (req) => {
        if (!localStorage.getItem("userToken")) {
          if (req.fetchOpts?.headers?.Authorization) {
            delete req.fetchOpts.headers.Authorization;
          }
        }
        return next(req);
      },
    ],
    { noThrow: true }
  );

  const source = new RecordSource();
  const store = new Store(source);
  return new Environment({ network, store });
};

/* -------------------------------------------------------------------------- */
/* 🧹 CLEANUP USER DATA + CLEAR BROWSER CACHE                                 */
/* -------------------------------------------------------------------------- */
export const cleanupUserData = async (navigate = null) => {
  [
    "userToken",
    "refreshToken",
    "userEmail",
    "userNumber",
    "moeID",
    "userID",
    "token",
    "currentLocationLatLong",
    "userName",
    "signinORsignup",
  ].forEach((key) => localStorage.removeItem(key));

  sessionStorage.clear();

  if (window.Moengage) {
    window.Moengage.destroy_session?.();
  }

  reduxStore.dispatch(clearUserData());
  reduxStore.dispatch(clearBookingDataOnLogoutAction());
  reduxStore.dispatch(postBookingDataAction({}));

  setTimeout(() => {
    if (navigate) navigate("/", { replace: true });
    else window.location.replace("/");
  }, 300);
};

/* -------------------------------------------------------------------------- */
/* 🚪 LOGOUT HANDLER (ENSURE CLEAN EXIT)                                     */
/* -------------------------------------------------------------------------- */
export const performLogout = (navigate = null) => {
  LogoutUserMutation.commit(environment)
    .then(() => cleanupUserData(navigate))
    .catch(() => cleanupUserData(navigate));
};

/* -------------------------------------------------------------------------- */
/* 🌍 INITIALIZE DEFAULT ENVIRONMENT                                          */
/* -------------------------------------------------------------------------- */
const environment = createRelayEnvironment();
export default environment;
